/**
 * Formatos dos registros da maternidade e da neonatologia (Módulo 6): internação, parto,
 * recém-nascido, exames e avaliação do RN, conduta (D-037), tratamento do RN (D-038) e
 * TARV da gestante (D-039). Os mesmos schemas validam o formulário e a API.
 */
import { z } from 'zod';
import { hojeEmBrasilia } from './esquemas-cadastro';
import { EXAMES_NAO_TREPONEMICOS, EXAMES_TREPONEMICOS, TITULOS } from './esquemas-prenatal';

const dataPassada = (rotulo: string) =>
  z.iso
    .date({ message: 'Informe uma data válida.' })
    .refine((d) => d <= hojeEmBrasilia(), { message: `${rotulo} não pode estar no futuro.` });

const hora = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, { message: 'Hora no formato 14:30.' })
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

const textoOpcional = (max = 2000) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const inteiroOpcional = (min: number, max: number, mensagem: string) =>
  z
    .union([z.number(), z.literal('').transform(() => null), z.null()])
    .optional()
    .refine((v) => v === null || v === undefined || (Number.isInteger(v) && v >= min && v <= max), {
      message: mensagem,
    });

// ---------------------------------------------------------------- códigos e rótulos
export const SIM_NAO_SINAN = ['sim', 'nao', 'nao_realizado', 'nao_se_aplica', 'ignorado'] as const;
export type SimNaoSinan = (typeof SIM_NAO_SINAN)[number];
export const ROTULO_SIM_NAO: Record<SimNaoSinan, string> = {
  sim: 'Sim',
  nao: 'Não',
  nao_realizado: 'Não realizado',
  nao_se_aplica: 'Não se aplica',
  ignorado: 'Ignorado',
};

export const TIPOS_PARTO = ['vaginal', 'forceps', 'cesarea_eletiva', 'cesarea_urgencia'] as const;
export type TipoParto = (typeof TIPOS_PARTO)[number] | 'cesariana' | 'nao_informado';
export const ROTULO_TIPO_PARTO: Record<TipoParto, string> = {
  vaginal: 'Vaginal',
  forceps: 'Vaginal com fórceps',
  cesarea_eletiva: 'Cesárea eletiva',
  cesarea_urgencia: 'Cesárea de urgência',
  cesariana: 'Cesárea',
  nao_informado: 'Não informado',
};

export const TIPOS_CONCEPTO = ['nascido_vivo', 'natimorto', 'aborto'] as const;
export type TipoConcepto = (typeof TIPOS_CONCEPTO)[number];
export const ROTULO_CONCEPTO: Record<TipoConcepto, string> = {
  nascido_vivo: 'Nascido vivo',
  natimorto: 'Natimorto',
  aborto: 'Aborto',
};

export const SEXOS_RN = ['feminino', 'masculino', 'indeterminado'] as const;
export const ROTULO_SEXO_RN: Record<(typeof SEXOS_RN)[number] | 'nao_informado', string> = {
  feminino: 'Feminino',
  masculino: 'Masculino',
  indeterminado: 'Indeterminado',
  nao_informado: 'Não informado',
};

export const LOCAIS_INTERNACAO_RN = [
  'alojamento_conjunto',
  'bercario',
  'uti_neonatal',
  'outro',
] as const;
export const ROTULO_LOCAL_RN: Record<
  (typeof LOCAIS_INTERNACAO_RN)[number] | 'nao_informado',
  string
> = {
  alojamento_conjunto: 'Alojamento conjunto',
  bercario: 'Berçário',
  uti_neonatal: 'UTI neonatal',
  outro: 'Outro',
  nao_informado: 'Não informado',
};

/** Item 47 da ficha de gestante HIV. */
export const PROFILAXIAS_ARV_RN = [
  'primeiras_24h',
  'apos_24h',
  'nao_se_aplica',
  'nao_realizado',
  'ignorado',
] as const;
export type ProfilaxiaArvRn = (typeof PROFILAXIAS_ARV_RN)[number];
export const ROTULO_PROFILAXIA_RN: Record<ProfilaxiaArvRn, string> = {
  primeiras_24h: 'Nas primeiras 24 horas',
  apos_24h: 'Após 24 horas do nascimento',
  nao_se_aplica: 'Não se aplica',
  nao_realizado: 'Não realizado',
  ignorado: 'Ignorado',
};

/** Item 65 da ficha de sífilis congênita. */
export const EVOLUCOES_RN = [
  'vivo',
  'obito_sifilis_congenita',
  'obito_outras_causas',
  'aborto',
  'natimorto',
  'ignorado',
] as const;
export type EvolucaoRn = (typeof EVOLUCOES_RN)[number];
export const ROTULO_EVOLUCAO_RN: Record<EvolucaoRn, string> = {
  vivo: 'Vivo',
  obito_sifilis_congenita: 'Óbito por sífilis congênita',
  obito_outras_causas: 'Óbito por outras causas',
  aborto: 'Aborto',
  natimorto: 'Natimorto',
  ignorado: 'Ignorado',
};

export const RESULTADOS_AVALIACAO = ['normal', 'alterado', 'nao_realizado'] as const;
export type ResultadoAvaliacao =
  (typeof RESULTADOS_AVALIACAO)[number] | 'inconclusivo' | 'nao_informado';
export const ROTULO_AVALIACAO: Record<ResultadoAvaliacao, string> = {
  normal: 'Normal',
  alterado: 'Alterado',
  nao_realizado: 'Não realizado',
  inconclusivo: 'Inconclusivo',
  nao_informado: 'Não informado',
};

/** Sinais e sintomas do item 63 da ficha de sífilis congênita (coluna → rótulo). */
export const SINAIS_RN = {
  sinal_ictericia: 'Icterícia',
  sinal_anemia: 'Anemia',
  sinal_esplenomegalia: 'Esplenomegalia',
  sinal_osteocondrite: 'Osteocondrite',
  sinal_rinite_muco_sanguinolenta: 'Rinite muco-sanguinolenta',
  sinal_hepatomegalia: 'Hepatomegalia',
  sinal_lesoes_cutaneas: 'Lesões cutâneas',
  sinal_pseudoparalisia: 'Pseudoparalisia',
} as const;
export type SinalRn = keyof typeof SINAIS_RN;
export const CHAVES_SINAIS_RN = Object.keys(SINAIS_RN) as SinalRn[];

/** Exames do RN: não treponêmicos (com título), treponêmicos e VDRL no líquor. */
export const EXAMES_RN_LABORATORIO = [
  ...EXAMES_NAO_TREPONEMICOS,
  ...EXAMES_TREPONEMICOS,
  'liquor',
] as const;
export const ROTULO_EXAME_RN: Record<(typeof EXAMES_RN_LABORATORIO)[number], string> = {
  vdrl: 'VDRL (sangue periférico)',
  rpr: 'RPR (sangue periférico)',
  teste_treponemico: 'Teste treponêmico',
  ftabs: 'FTA-Abs',
  tpha: 'TPHA',
  liquor: 'VDRL no líquor',
};

/** Item 64 da ficha de sífilis congênita (D-038). */
export const ESQUEMAS_RN = [
  'cristalina_10_dias',
  'procaina_10_dias',
  'benzatina_dose_unica',
  'outro',
  'nao_realizado',
] as const;
export type EsquemaRn = (typeof ESQUEMAS_RN)[number];
export const ROTULO_ESQUEMA_RN: Record<EsquemaRn, string> = {
  cristalina_10_dias: 'Penicilina G cristalina, 10 dias',
  procaina_10_dias: 'Penicilina G procaína, 10 dias',
  benzatina_dose_unica: 'Penicilina G benzatina, dose única',
  outro: 'Outro esquema',
  nao_realizado: 'Não realizado',
};
export const CODIGO_ESQUEMA_RN: Record<EsquemaRn, string> = {
  cristalina_10_dias: '1',
  procaina_10_dias: '2',
  benzatina_dose_unica: '3',
  outro: '4',
  nao_realizado: '5',
};

export const CLASSIFICACOES_NEONATAIS = [
  'crianca_exposta',
  'avaliacao_storch',
  'tratamento_benzatina_dose_unica',
  'sifilis_congenita_sem_neurossifilis',
  'sifilis_congenita_com_neurossifilis',
] as const;
export type ClassificacaoNeonatal = (typeof CLASSIFICACOES_NEONATAIS)[number];

// ---------------------------------------------------------------- internação, parto, alta
export const internacaoSchema = z.object({
  /** Obrigatória só para quem não tem unidade fixa. */
  unidadeId: z.uuid({ message: 'Escolha a maternidade.' }).nullable().optional(),
  dataInternacao: dataPassada('A data da internação'),
  horaInternacao: hora,
  observacoes: textoOpcional(),
});
export type DadosInternacao = z.infer<typeof internacaoSchema>;

export const altaSchema = z.object({
  dataAlta: dataPassada('A data da alta'),
  horaAlta: hora,
  observacoes: textoOpcional(),
});
export type DadosAlta = z.infer<typeof altaSchema>;

export const partoSchema = z
  .object({
    dataParto: dataPassada('A data do parto'),
    horaParto: hora,
    tipoParto: z.enum(TIPOS_PARTO, { message: 'Escolha o tipo de parto.' }),
    indicacaoCesariana: textoOpcional(500),
    /** Item 45 da ficha de gestante HIV. */
    profilaxiaArvNoParto: z.enum(['sim', 'nao', 'ignorado']).nullable().optional(),
    intercorrencias: textoOpcional(),
  })
  .refine((p) => p.tipoParto !== 'cesarea_urgencia' || Boolean(p.indicacaoCesariana), {
    path: ['indicacaoCesariana'],
    message: 'Informe a indicação da cesárea de urgência.',
  });
export type DadosParto = z.infer<typeof partoSchema>;

// ---------------------------------------------------------------- recém-nascido
export const recemNascidoSchema = z
  .object({
    tipoConcepto: z.enum(TIPOS_CONCEPTO),
    dataNascimento: dataPassada('A data do nascimento'),
    horaNascimento: hora,
    sexo: z.enum([...SEXOS_RN, 'nao_informado']).default('nao_informado'),
    pesoGramas: inteiroOpcional(100, 8000, 'Peso entre 100 e 8000 gramas.'),
    apgar1: inteiroOpcional(0, 10, 'Apgar de 0 a 10.'),
    apgar5: inteiroOpcional(0, 10, 'Apgar de 0 a 10.'),
    localInternacao: z.enum([...LOCAIS_INTERNACAO_RN, 'nao_informado']).default('nao_informado'),
    profilaxiaArvInicio: z.enum(PROFILAXIAS_ARV_RN).nullable().optional(),
    evolucaoCaso: z.enum(EVOLUCOES_RN).nullable().optional(),
    dataObito: dataPassada('A data do óbito').nullable().optional(),
    observacoes: textoOpcional(),
  })
  .refine(
    (r) =>
      !r.dataObito ||
      r.evolucaoCaso === 'obito_sifilis_congenita' ||
      r.evolucaoCaso === 'obito_outras_causas',
    { path: ['dataObito'], message: 'Data do óbito só quando a evolução for óbito.' },
  )
  .refine(
    (r) =>
      !(r.evolucaoCaso === 'obito_sifilis_congenita' || r.evolucaoCaso === 'obito_outras_causas') ||
      Boolean(r.dataObito),
    { path: ['dataObito'], message: 'Informe a data do óbito.' },
  )
  .refine((r) => !r.dataObito || r.dataObito >= r.dataNascimento, {
    path: ['dataObito'],
    message: 'O óbito não pode ser anterior ao nascimento.',
  });
export type DadosRecemNascido = z.infer<typeof recemNascidoSchema>;

/** Avaliação clínica do RN (respostas "informadas" do fluxograma — D-037). */
export const avaliacaoClinicaRnSchema = z.object({
  exameFisico: z.enum(RESULTADOS_AVALIACAO).nullable().optional(),
  exameFisicoDescricao: textoOpcional(),
  hemograma: z.enum(RESULTADOS_AVALIACAO).nullable().optional(),
  liquor: z.enum(RESULTADOS_AVALIACAO).nullable().optional(),
  liquorObservacoes: textoOpcional(),
  /** D-058: valores opcionais do líquor; com eles, o sistema sugere normal/alterado. */
  liquorDataColeta: dataPassada('A data da coleta do líquor').nullable().optional(),
  liquorLeucocitos: z
    .number({ message: 'Informe um número.' })
    .int({ message: 'Use um número inteiro.' })
    .min(0, { message: 'Não pode ser negativo.' })
    .max(100000, { message: 'Confira o valor.' })
    .nullable()
    .optional(),
  liquorProteinas: z
    .number({ message: 'Informe um número.' })
    .int({ message: 'Use um número inteiro.' })
    .min(0, { message: 'Não pode ser negativo.' })
    .max(10000, { message: 'Confira o valor.' })
    .nullable()
    .optional(),
  rxOssosLongos: z.enum(RESULTADOS_AVALIACAO).nullable().optional(),
  evidenciaTreponema: z.enum(['sim', 'nao', 'nao_realizado', 'ignorado']).nullable().optional(),
  sinais: z.partialRecord(z.enum(CHAVES_SINAIS_RN), z.enum(['sim', 'nao'])).default({}),
  sinalOutro: textoOpcional(120),
});
export type DadosAvaliacaoClinicaRn = z.infer<typeof avaliacaoClinicaRnSchema>;

export const exameRnSchema = z
  .object({
    tipoExame: z.enum(EXAMES_RN_LABORATORIO),
    dataColeta: dataPassada('A data da coleta'),
    resultado: z.enum(['reagente', 'nao_reagente', 'aguardando_resultado', 'nao_realizado'], {
      message: 'Escolha o resultado.',
    }),
    titulo: z.enum(TITULOS).nullable().optional(),
    observacoes: textoOpcional(),
  })
  .refine(
    (e) =>
      ![...EXAMES_NAO_TREPONEMICOS, 'liquor'].includes(e.tipoExame) ||
      e.resultado !== 'reagente' ||
      Boolean(e.titulo),
    { path: ['titulo'], message: 'Informe o título (ex.: 1:8).' },
  )
  .refine(
    (e) =>
      !e.titulo ||
      (e.resultado === 'reagente' && [...EXAMES_NAO_TREPONEMICOS, 'liquor'].includes(e.tipoExame)),
    { path: ['titulo'], message: 'Título só se aplica a VDRL/RPR reagente.' },
  );
export type DadosExameRn = z.infer<typeof exameRnSchema>;

// ---------------------------------------------------------------- conduta e tratamento
/** D-037: o médico confirma a classificação; divergir da sugestão exige justificativa. */
export const confirmarCondutaSchema = z
  .object({
    classificacao: z.enum(CLASSIFICACOES_NEONATAIS, { message: 'Escolha a classificação.' }),
    justificativa: textoOpcional(1000),
    /** Correção da adequação do tratamento materno calculada (D-004), com justificativa. */
    maeAdequada: z.boolean().nullable().optional(),
    justificativaAdequacao: textoOpcional(1000),
  })
  .refine((c) => c.maeAdequada == null || (c.justificativaAdequacao?.length ?? 0) >= 10, {
    path: ['justificativaAdequacao'],
    message: 'Explique por que a adequação difere do cálculo (mínimo de 10 caracteres).',
  });
export type DadosConfirmarConduta = z.infer<typeof confirmarCondutaSchema>;

export const tratamentoRnSchema = z
  .object({
    esquema: z.enum(ESQUEMAS_RN, { message: 'Escolha o esquema.' }),
    esquemaOutro: textoOpcional(255),
    dataInicio: dataPassada('A data de início').nullable().optional(),
    dataFim: dataPassada('A data de término').nullable().optional(),
    /** Benzatina dose única: hora e lote da aplicação. */
    horaAplicacao: hora,
    lote: textoOpcional(40),
    observacoes: textoOpcional(),
  })
  .refine((t) => t.esquema === 'nao_realizado' || Boolean(t.dataInicio), {
    path: ['dataInicio'],
    message: 'Informe a data de início.',
  })
  .refine((t) => t.esquema !== 'outro' || Boolean(t.esquemaOutro), {
    path: ['esquemaOutro'],
    message: 'Descreva o esquema.',
  })
  .refine((t) => !t.dataFim || (t.dataInicio && t.dataFim >= t.dataInicio), {
    path: ['dataFim'],
    message: 'O término não pode ser anterior ao início.',
  });
export type DadosTratamentoRn = z.infer<typeof tratamentoRnSchema>;

export const terminoTratamentoRnSchema = z.object({ dataFim: dataPassada('A data de término') });

/** D-039: TARV da gestante (itens 38 e 39 da ficha de gestante HIV). */
export const tarvGestanteSchema = z.object({
  dataInicio: dataPassada('A data de início'),
  observacoes: textoOpcional(),
});
export type DadosTarvGestante = z.infer<typeof tarvGestanteSchema>;
